import { apiClient } from './api';

export type StakeholderEntityType = 'RESTAURANT' | 'COURIER';

export interface StakeholderDocumentItem {
  id: string;
  documentType: string;
  documentTypeLabel: string;
  originalFilename: string;
  mimeType: string;
  fileSizeBytes: string;
  uploadedByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformLegalDocumentItem {
  id: string;
  documentType: string;
  documentTypeLabel: string;
  version: number;
  title: string;
  isActive: boolean;
  publishedAt: string;
  acceptanceCount: number;
}

export interface RestaurantApplicationFormData {
  legalName: string;
  tradeName: string;
  taxOffice: string;
  taxNumber: string;
  mersisNumber: string;
  address: string;
  phone: string;
  email: string;
  authorizedPersonName: string;
  authorizedPersonPhone: string;
  bankAccountHolder: string;
  iban: string;
  formDate: string;
}

export interface LegalStatusResponse {
  requiresAcceptance: boolean;
  document: {
    id: string;
    version: number;
    title: string;
    viewUrl: string;
  } | null;
  acceptance: {
    acceptedAt: string;
    acceptedByUserId: string;
  } | null;
}

export const RESTAURANT_DOCUMENT_TYPES = [
  { value: 'SOZLESME', label: 'Sözleşme' },
  { value: 'VERGI_LEVHASI', label: 'Vergi Levhası' },
  { value: 'ISLETME_RUHSATI', label: 'İşletme Ruhsatı / Ticaret Sicil' },
  { value: 'YETKILI_KIMLIK', label: 'Yetkili Kimlik Fotokopisi' },
  { value: 'IBAN_BELEGE', label: 'IBAN / Banka Hesap Belgesi' },
] as const;

export const COURIER_DOCUMENT_TYPES = [
  { value: 'SOZLESME', label: 'Taşıyıcı Sözleşmesi' },
  { value: 'KIMLIK', label: 'Kimlik Fotokopisi' },
  { value: 'EHLIYET', label: 'Ehliyet' },
  { value: 'ARAC_RUHSATI', label: 'Araç Ruhsatı' },
  { value: 'SRC_BELGESI', label: 'SRC Belgesi' },
] as const;

export const complianceService = {
  async listStakeholderDocuments(
    entityType: StakeholderEntityType,
    entityId: string,
  ): Promise<StakeholderDocumentItem[]> {
    const response = await apiClient.get(
      `/admin/stakeholders/${entityType}/${entityId}/documents`,
    );
    return response.data.data;
  },

  async uploadStakeholderDocument(
    entityType: StakeholderEntityType,
    entityId: string,
    documentType: string,
    file: File,
  ): Promise<StakeholderDocumentItem> {
    const formData = new FormData();
    formData.append('documentType', documentType);
    formData.append('file', file);
    const response = await apiClient.post(
      `/admin/stakeholders/${entityType}/${entityId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data.data;
  },

  async getStakeholderDocumentDownloadUrl(
    entityType: StakeholderEntityType,
    entityId: string,
    documentId: string,
  ): Promise<string> {
    const response = await apiClient.get(
      `/admin/stakeholders/${entityType}/${entityId}/documents/${documentId}/download-url`,
    );
    return response.data.data.downloadUrl;
  },

  async deleteStakeholderDocument(
    entityType: StakeholderEntityType,
    entityId: string,
    documentId: string,
  ): Promise<void> {
    await apiClient.delete(
      `/admin/stakeholders/${entityType}/${entityId}/documents/${documentId}`,
    );
  },

  async listPlatformLegalDocuments(): Promise<PlatformLegalDocumentItem[]> {
    const response = await apiClient.get('/admin/platform-legal-documents');
    return response.data.data;
  },

  async publishPlatformLegalDocument(title: string, file: File) {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('file', file);
    const response = await apiClient.post('/admin/platform-legal-documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  async getPlatformLegalDocumentDownloadUrl(documentId: string): Promise<string> {
    const response = await apiClient.get(
      `/admin/platform-legal-documents/${documentId}/download-url`,
    );
    return response.data.data.downloadUrl;
  },

  async getApplicationForm(restaurantId: string) {
    const response = await apiClient.get(`/admin/restaurants/${restaurantId}/application-form`);
    return response.data.data as {
      restaurantId: string;
      formData: RestaurantApplicationFormData;
      prefill?: RestaurantApplicationFormData;
      lastGeneratedAt: string | null;
      updatedAt: string;
    };
  },

  async saveApplicationForm(restaurantId: string, formData: Partial<RestaurantApplicationFormData>) {
    const response = await apiClient.put(`/admin/restaurants/${restaurantId}/application-form`, {
      formData,
    });
    return response.data.data;
  },

  async generateApplicationFormPdf(restaurantId: string): Promise<Blob> {
    const response = await apiClient.post(
      `/admin/restaurants/${restaurantId}/application-form/generate-pdf`,
      {},
      { responseType: 'blob' },
    );
    return response.data;
  },

  async listMyDocuments(): Promise<StakeholderDocumentItem[]> {
    const response = await apiClient.get('/restaurant/my/documents');
    return response.data.data;
  },

  async uploadMyDocument(documentType: string, file: File): Promise<StakeholderDocumentItem> {
    const formData = new FormData();
    formData.append('documentType', documentType);
    formData.append('file', file);
    const response = await apiClient.post('/restaurant/my/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  async getMyDocumentDownloadUrl(documentId: string): Promise<string> {
    const response = await apiClient.get(`/restaurant/my/documents/${documentId}/download-url`);
    return response.data.data.downloadUrl;
  },

  async getLegalStatus(): Promise<LegalStatusResponse> {
    const response = await apiClient.get('/restaurant/my/legal-status');
    return response.data.data;
  },

  async acceptLegalDocument(legalDocumentId: string) {
    const response = await apiClient.post('/restaurant/my/legal-acceptance', {
      legalDocumentId,
    });
    return response.data.data;
  },

  async getMyApplicationForm() {
    const response = await apiClient.get('/restaurant/my/application-form');
    return response.data.data as {
      restaurantId: string;
      formData: RestaurantApplicationFormData;
      prefill?: RestaurantApplicationFormData;
      lastGeneratedAt: string | null;
      updatedAt: string;
    };
  },

  async saveMyApplicationForm(formData: Partial<RestaurantApplicationFormData>) {
    const response = await apiClient.put('/restaurant/my/application-form', { formData });
    return response.data.data;
  },

  async generateMyApplicationFormPdf(): Promise<Blob> {
    const response = await apiClient.post(
      '/restaurant/my/application-form/generate-pdf',
      {},
      { responseType: 'blob' },
    );
    return response.data;
  },
};
